import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  View,
  Card,
  Text,
  Button,
  Flex,
  Loader,
  Badge,
} from "@aws-amplify/ui-react";
import { useSubscriptionContext } from '../contexts/SubscriptionContext';

interface ProGateProps {
  children: ReactNode;
  showSubscriptionSource?: boolean; // Optional prop to show subscription source info
}

/**
 * ProGate component - Wraps Pro-only features and shows upgrade prompt for non-Pro users
 * Checks both individual and company subscription status
 * Displays appropriate messaging based on subscription source
 */
const ProGate = ({
  children,
  showSubscriptionSource = false,
}: ProGateProps) => {
  const {
    isPro,
    loading,
    subscriptionSource,
    companyDomain,
    getUpgradeMessage,
  } = useSubscriptionContext();
  const navigate = useNavigate();

  if (loading) {
    return (
      <View padding="2rem" textAlign="center">
        <Loader size="large" />
        <Text marginTop="1rem">Checking subscription status...</Text>
      </View>
    );
  }

  // If user has Pro access, show children with optional subscription source info
  if (isPro) {
    return (
      <View>
        {showSubscriptionSource && subscriptionSource && (
          <View marginBottom="1rem">
            <Flex alignItems="center" gap="0.5rem">
              <Badge
                variation={
                  subscriptionSource === "company" ? "info" : "success"
                }
                size="small"
              >
                {subscriptionSource === "company"
                  ? `Company Pro (${companyDomain})`
                  : "Individual Pro"}
              </Badge>
            </Flex>
          </View>
        )}
        {children}
      </View>
    );
  }

  // User doesn't have Pro access - show upgrade prompt with appropriate messaging
  return (
    <View padding="2rem">
      <Card
        variation="elevated"
        padding="2rem"
        textAlign="center"
        backgroundColor="var(--amplify-colors-background-secondary)"
      >
        <Text fontSize="2rem" marginBottom="1rem">
          🔒
        </Text>
        <Text fontSize="1.5rem" fontWeight="bold" marginBottom="1rem">
          Pro Feature
        </Text>
        <Text marginBottom="2rem" color="var(--amplify-colors-font-secondary)">
          {getUpgradeMessage()}
        </Text>

        {/* Show different action buttons based on company email status */}
        <Flex direction="column" gap="1rem" alignItems="center">
          {companyDomain ? (
            // User has verified company email but no company subscription
            <>
              <Button
                variation="primary"
                onClick={() => navigate("/settings/company-subscription")}
              >
                Company Subscription
              </Button>
              <Text
                fontSize="small"
                color="var(--amplify-colors-font-secondary)"
              >
                Ask your admin to purchase a company subscription for{" "}
                {companyDomain}
              </Text>
              <Text fontSize="small" marginTop="0.5rem">
                or
              </Text>
              <Button
                variation="primary"
                onClick={() => navigate("/settings/subscription")}
              >
                Get Individual Pro
              </Button>
            </>
          ) : (
            // User doesn't have verified company email
            <>
              <Button
                variation="primary"
                onClick={() => navigate("/settings/subscription")}
              >
                Upgrade to Pro
              </Button>
              <Button
                variation="link"
                onClick={() => navigate("/settings/subscription")}
              >
                Start 14-Day Free Trial
              </Button>
              <Text
                fontSize="small"
                color="var(--amplify-colors-font-secondary)"
                marginTop="1rem"
              >
                Have a company email?
                <Button
                  variation="link"
                  size="small"
                  onClick={() => navigate("/settings/subscription")}
                >
                  Verify it in Settings
                </Button>
                to check for company subscriptions
              </Text>
            </>
          )}
        </Flex>
      </Card>
    </View>
  );
};

export default ProGate;
